import React, { useState } from 'react';
import { 
  Box, TextField, Button, Typography, Container, Paper, CircularProgress, Link, InputAdornment, IconButton 
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { loginUser } from '../Service/auth-service';
import { useNavigate } from 'react-router-dom';

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await loginUser(formData);
      if (data.status) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'center',
      p: 2
    }}>
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ 
          p: { xs: 4, sm: 6 }, 
          borderRadius: 4, 
          boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center' 
        }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              p: 2, 
              borderRadius: '50%', 
              bgcolor: '#e3f2fd', 
              color: '#2196f3',
              mb: 2 
            }}>
              <StorefrontIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h4" fontWeight="900" sx={{ color: '#2c3e50', letterSpacing: -0.5 }}>
              SHOPMANAGER
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontWeight: 500 }}>
              Welcome back! Please enter your details.
            </Typography>
          </Box>

          {errorMsg && (
            <Box sx={{ width: '100%', bgcolor: '#ffebee', color: '#c62828', p: 1.5, borderRadius: 2, mb: 3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, border: '1px solid #ffcdd2' }}>
              {errorMsg}
            </Box>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Stack spacing={3} sx={{ width: '100%', alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                required
                InputProps={{ sx: { borderRadius: 2 } }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={formData.password}
                onChange={handleChange}
                required
                InputProps={{ 
                  sx: { borderRadius: 2 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                disableElevation
                sx={{ 
                  py: 1.5,
                  px: 8,
                  borderRadius: 2, 
                  fontWeight: 800, 
                  bgcolor: '#2196f3',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#1976d2' }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Stack>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
              New to the platform?{' '}
              <Link href="/register" underline="none" sx={{ color: '#2196f3', fontWeight: 700 }}>
                Create an account
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

function Stack({ children, spacing, sx, ...props }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing, ...sx }} {...props}>
            {children}
        </Box>
    );
}

export default Login;